/**
 * DocumentService - Servis za upravljanje dokumentima
 * 
 * Omogućava komunikaciju sa Document API endpoint-ima:
 * - POST /document/upload - Upload dokumenta
 * - POST /document/search - Pretraga dokumenata
 * - POST /document/download - Download dokumenta
 * - POST /document/delete - Brisanje dokumenta
 */

import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';

class DocumentService {
    /**
     * Uploaduje dokument na server
     * @param {File} file - Fajl koji se uploaduje
     * @param {string} name - Naziv dokumenta
     * @param {Array<string>} relatedEntityIds - ID-evi povezanih entiteta
     * @returns {Promise<Object>} Odgovor servera
     */
    async upload(file, name, relatedEntityIds = []) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', name);

        // Comma-separated bez razmaka — razmak posle zareza lomi parsiranje na nekim BE implementacijama
        if (relatedEntityIds && relatedEntityIds.length > 0) {
            formData.append(
                'relatedEntityIds',
                relatedEntityIds.map((e) => String(e).trim()).filter(Boolean).join(','),
            );
        }

        try {
            // Koristimo ApiService.post koji automatski dodaje Authorization header
            // Content-Type mora biti undefined da bi browser automatski postavio boundary za FormData
            const response = await ApiService.post(API_ENDPOINTS.DOCUMENT.UPLOAD, formData, {
                'Content-Type': undefined,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pretražuje dokumente na osnovu entiteta
     * @param {Array<string>} entityIds - Niz ID-eva entiteta za pretragu
     * @returns {Promise<Array>} Lista dokumenata
     */
    async search(entityIds = [], signal = null) {
        try {
            const normalizedIds = (entityIds || []).map((e) => String(e).trim()).filter(Boolean);
            const response = await ApiService.post(
                API_ENDPOINTS.DOCUMENT.SEARCH,
                { entity_ids: normalizedIds },
                {},
                signal
            );
            // API vraća { documents: [...] }, izvuci samo documents niz
            // Proveri različite moguće formate odgovora
            if (Array.isArray(response.data)) {
                return response.data;
            } else if (response.data?.documents && Array.isArray(response.data.documents)) {
                return response.data.documents;
            } else if (response.data?.data && Array.isArray(response.data.data)) {
                return response.data.data;
            }
            return [];
        } catch (error) {
            throw error;
        }
    }

    /**
     * Downloaduje dokument
     * @param {string} documentId - ID dokumenta
     * @returns {Promise<Blob>} Blob fajla
     */
    async download(documentId) {
        try {
            // Koristimo callApi direktno da bismo mogli proslediti responseType
            const response = await ApiService.callApi({
                method: 'POST',
                url: API_ENDPOINTS.DOCUMENT.DOWNLOAD,
                data: { document_id: documentId },
                responseType: 'blob' // Važno za download fajlova
            });
            
            // Proveri da li je response uspešan i da li je stvarno blob
            if (response.status === 200 && response.data instanceof Blob) {
                return response.data;
            }
            
            // Ako response nije blob, pokušaj da parsiraš kao JSON grešku
            if (response.data instanceof Blob) {
                try {
                    const text = await response.data.text();
                    const errorData = JSON.parse(text);
                    throw new Error(errorData.message || 'Грешка при преузимању документа');
                } catch (parseError) {
                    // Ako ne može da se parsira, verovatno je validan PDF
                    return response.data;
                }
            }
            
            throw new Error('Неочекиван формат одговора од сервера');
        } catch (error) {
            // Ako je error response blob (što znači da backend vraća grešku kao PDF/JSON)
            // pokušaj da parsiraš kao JSON da vidiš šta je greška
            if (error.response && error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const errorData = JSON.parse(text);
                    console.error('Download error from server:', errorData);
                    throw new Error(errorData.message || 'Грешка при преузимању документа');
                } catch (parseError) {
                    // Ako ne može da se parsira kao JSON, verovatno je PDF greška
                    console.error('Download error - response is not JSON:', error);
                    throw new Error('Грешка при преузимању документа');
                }
            }
            
            // Ako error nema response, baci originalnu grešku
            if (error.message) {
                throw error;
            }
            
            throw new Error('Грешка при преузимању документа');
        }
    }

    /**
     * Briše dokument.
     * Endpoint: POST /document/delete
     * @param {string} documentId - ID dokumenta
     * @returns {Promise<Object>} Odgovor servera
     */
    async delete(documentId) {
        const response = await ApiService.post(API_ENDPOINTS.DOCUMENT.DELETE, {
            document_id: String(documentId),
        });
        return response.data;
    }

    /**
     * Označava da li se dokument može komentarisati (PDF konsultacije).
     * Endpoint: POST /document/toggle-comment
     * @param {Array<string>} documentIds
     * @param {boolean} comment
     * @returns {Promise<Object>} Odgovor servera
     */
    async toggleComment(documentIds = [], comment = true) {
        try {
            const ids = Array.isArray(documentIds) ? documentIds : [documentIds].filter(Boolean);
            const response = await ApiService.post(API_ENDPOINTS.DOCUMENT.TOGGLE_COMMENT, {
                document_ids: ids.map((id) => String(id)),
                comment: !!comment,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Učitava komentare za dokument.
     * Endpoint: POST /document/comment/search
     * @param {string} documentId
     * @param {AbortSignal|null} signal
     * @returns {Promise<Array>} Lista komentara
     */
    async searchComments(documentId, signal = null) {
        try {
            const response = await ApiService.post(
                API_ENDPOINTS.DOCUMENT.COMMENT_SEARCH,
                { document_id: String(documentId) },
                {},
                signal
            );
            const data = response?.data;
            const comments = data?.comments;
            return Array.isArray(comments) ? comments : [];
        } catch (error) {
            throw error;
        }
    }

    /**
     * Kreira/menja komentar na dokumentu.
     * Endpoint: POST /document/comment/save-or-update
     * @param {Object} payload
     * @returns {Promise<Object>} Odgovor servera
     */
    async saveOrUpdateComment(payload) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.DOCUMENT.COMMENT_SAVE_OR_UPDATE, payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Menja status komentara na dokumentu (admin moderacija).
     * Endpoint: POST /document/comment/status/change
     * @param {{ commentId: string, status: string, comment?: string }} params
     * @returns {Promise<Object>} Odgovor servera
     */
    async changeCommentStatus({ commentId, status, comment = '' }) {
        const payload = {
            comment_id: String(commentId),
            status: String(status),
        };
        const trimmed = String(comment || '').trim();
        if (trimmed) {
            payload.comment = trimmed;
        }
        try {
            const response = await ApiService.post(API_ENDPOINTS.DOCUMENT.COMMENT_STATUS_CHANGE, payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
}

const documentServiceInstance = new DocumentService();
export default documentServiceInstance;
